import { FC, useEffect, useMemo, useState } from "react";
import Modal from "../Modal";
import { FormikProps } from "formik";
import ChangeModal from "./ChangeModal";
import { useAppSelector } from "../../store/hooks";
import { PrimaryButton } from "../Buttons";
import { alertService, getBankList, listBankBC } from "../../services";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import WarningModal from "../WarningModal";
import {
  Currency,
  currencyExchange,
  formatGooglePhone,
  currencyExchangeText,
  CurrencyExchanges,
} from "../../utils";
import {
  AccountInterface,
  Banco,
  PaymentInterface,
  PaymentMethodEnum,
  PaymentMethodInterface,
  PaymentStatusEnum,
} from "../../interfaces";

export interface PaymentFormValues {
  amount: number;
  clientIdentifier: string;
  paymentMethod: PaymentMethodInterface;
  bank: Banco;
  phone: string;
  email: string | undefined;
  reference: string;
  currency: Currency;
  destBankID: number | undefined;
}

interface PaymentConfirmModalProps {
  formik: FormikProps<PaymentFormValues>;
  owner: AccountInterface;
  remaining: number;
  payments: PaymentInterface[];
  exchanges: CurrencyExchanges;
  detailView?: boolean;
  availableChange: number;
  paymentConfirmationModalClose: boolean;
  setPaymentConfirmationModalClose: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  onSubmit: (payment: PaymentInterface) => void;
}

const PaymentChange: FC<PaymentConfirmModalProps> = ({
  formik,
  owner,
  remaining,
  payments,
  exchanges,
  detailView = false,
  availableChange,
  paymentConfirmationModalClose,
  setPaymentConfirmationModalClose,
  onSubmit,
}) => {
  const paymentMethods = useAppSelector(
    (state) => state.user.paymentMethods ?? []
  );
  const [changeModal, setChangeModal] = useState(false);
  const [pendingChange, setPendingChange] = useState(false);
  const [firstOpenModal, setFirstOpenModal] = useState(true);
  const [reintegrationTypeModal, setReintegrationTypeModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [refundConfirmationModal, setRefundConfirmationModal] = useState(false);
  const [isNatural, setIsNatural] = useState(true);
  const [b2pBank, setB2pBank] = useState<Banco[]>([]);
  const [bankList, setBankList] = useState<Banco[]>([]);
  const [changeModalTab, setChangeModalTab] = useState(0);
  const [isRefund, setIsRefund] = useState(false);
  const [unavailableRefundModal, setUnavailableRefundModal] = useState(false);

  const user = useAppSelector((state) => state.user.user);

  // unused payment method reintegro
  const refundMethod = useMemo(
    () =>
      paymentMethods.find(
        (e) => e.paymentMethodID === PaymentMethodEnum.REINTEGRO
  ), [paymentMethods]);

  const hasEligibleZelleReintegration = useMemo(() => {
    const filteredPayments = payments.filter(
      (p) => p.paymentMethod.paymentMethodID !== PaymentMethodEnum.AJUSTE_DECIMAL &&
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL &&
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.RETENCION_DE_ISRL &&
        p.paymentMethod.paymentMethodID !== PaymentMethodEnum.RETENCION_DE_IVA &&
        p.status === PaymentStatusEnum.APPROVE
    );
    const lastPayment = filteredPayments[filteredPayments.length - 1];

    return lastPayment &&
      lastPayment.paymentMethod.paymentMethodID === PaymentMethodEnum.ZELLE &&
      lastPayment.status === PaymentStatusEnum.APPROVE;

  }, [payments]);

  const instantCreditCMMethod = useMemo(
    () =>
      paymentMethods.find(
        (e) => e.paymentMethodID === PaymentMethodEnum.CREDITO_INMEDIATO_CM
      ),
    [paymentMethods]
  );

  const hasRetention = useMemo(() => {
    return payments.some(
      (payment) =>
        payment.isRetention &&
        (payment.status === PaymentStatusEnum.PENDING ||
          payment.status === PaymentStatusEnum.APPROVE)
    );
  }, [payments]);

  const isRefundable = useMemo(() => {
    return !payments.some(
      (payment) => payment.status === PaymentStatusEnum.PENDING
    );
  }, [payments]);

  useEffect(() => {
    // Get b2p bank list
    const listBank = async () => {
      const res: Banco[] = await listBankBC("b2p");
      if (res !== null) {
        setB2pBank(res);
      }
    };
    const getBanks = async () => {
      const res = await getBankList();
      if (res.model !== null && !res.didError) {
        setBankList(res.model.filter((bank) => bank.codigo !== "0001"));
      }
    };

    listBank();
    getBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      remaining < -0.1 &&
      firstOpenModal &&
      !detailView &&
      paymentConfirmationModalClose
    ) {
      if (
        currencyExchange(availableChange, exchanges, "BS", "USD") <
          -remaining ||
        hasRetention
      )
        setRefundModal(true);
      else setChangeModal(true);
      setPaymentConfirmationModalClose(false);
      setFirstOpenModal(false);
    }

    // Set amount to pay in Bs
    let change = +currencyExchange(-remaining, exchanges, "BS").toFixed(2);
    formik.setFieldValue("amount", change);

    // Update formik when formik values change will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    remaining,
    firstOpenModal,
    paymentConfirmationModalClose,
    availableChange,
  ]);

  // Set client identifier and phone
  useEffect(() => {
    formik.setFieldValue(
      "clientIdentifier",
      // slice last char on string
      `${owner?.abreviationName?.[0]}${owner?.identificationNumber}`
    );
    formik.setFieldValue(
      "phone",
      owner?.listAccountPhone?.[0]?.phoneNumber
        ? formatGooglePhone(owner?.listAccountPhone?.[0]?.phoneNumber)
        : ""
    );

    // Check if owner is natural or legal person
    if (owner?.abreviationName !== "V-" && owner?.abreviationName !== "E-")
      setIsNatural(false);
    else setIsNatural(true);

    // Update formik when formik values change will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  useEffect(() => {
    for (const payment of payments) {
      if (
        payment.status === PaymentStatusEnum.PENDING &&
        [
          PaymentMethodEnum.REINTEGRO,
          PaymentMethodEnum.REINTEGRO_ZELLE,
          PaymentMethodEnum.CREDITO_INMEDIATO_CM,
          PaymentMethodEnum.CREDITO_INMEDIATO_CUENTA,
          PaymentMethodEnum.PAGO_MOVIL_B2P_VUELTO,
        ].includes(payment.paymentMethod.paymentMethodID)
      ) {
        setPendingChange(true);
        break;
      }
    }
  }, [payments]);

  return (
    <>
      <div className="flex flex-row items-center justify-end">
        <div className="flex flex-row items-start justify-end">
          {pendingChange ? (
            <p className="text-sm font-semibold text-gray-600">
              Hay un reintegro pendiente por aprobar
            </p>
          ) : (
            <>
              <h2 className="text-md font-semibold px-2 items-center">
                Cambio pendiente:
              </h2>
              <div className="flex flex-row justify-start pt-0.5">
                <p className="text-sm font-semibold text-gray-600 truncate place-self-center">
                  {currencyExchangeText(Math.abs(remaining), exchanges, "BS")}
                </p>
                <p className="text-xs text-gray-400 place-self-center ml-1">
                  {currencyExchangeText(Math.abs(remaining), exchanges, "USD")}
                </p>
              </div>
            </>
          )}
        </div>
        <div hidden={user?.roleName === "Consulta Innovus"}>
          <PrimaryButton
            className="ml-4"
            disabled={pendingChange}
            onClick={() => {
              if (isRefundable) {
                if (
                  currencyExchange(availableChange, exchanges, "BS", "USD") <
                    -remaining ||
                  hasRetention
                )
                  setRefundModal(true);
                else setChangeModal(true);
              } else {
                setUnavailableRefundModal(true);
              }
            }}
          >
            Dar cambio
          </PrimaryButton>
        </div>
      </div>

      {/* Refund modal */}
      <WarningModal
        openModal={refundModal}
        setOpenModal={setRefundModal}
        message={
          hasRetention
            ? "La guía tiene retenciones asociadas. Por lo que es necesario iniciar un proceso de reintegro por Casa Matriz."
            : "No hay saldo suficiente para dar cambio por crédito inmediato de forma automática. ¿Quiere iniciar un proceso de reintegro por Casa Matriz o usar otro método de cambio?"
        }
        secondaryButtonText="Cancelar"
        primaryButtonText="Aceptar"
        onPrimaryButton={() => {
          if (!instantCreditCMMethod) {
            alertService.error("Método de crédito inmediato CM no habilitado");
            setRefundModal(false);
            return;
          }
          setRefundModal(false);

          formik.setFieldValue("currency", "BS");
          formik.setFieldValue("paymentMethod", instantCreditCMMethod);
          setIsRefund(true);
          setChangeModal(true);
        }}
        onSecondaryButton={() => {
          setRefundModal(false);
        }}
        showSecondaryButton={true}
      />

      {/* Unavailable refund modal */}
      <WarningModal
        openModal={unavailableRefundModal}
        setOpenModal={setUnavailableRefundModal}
        message="No es posible procesar un cambio con pagos o retenciones pendientes por conciliar."
        onPrimaryButton={() => {
          setUnavailableRefundModal(false);
        }}
      />

      {/* Reintegration Type Modal */}
      <Modal
        openModal={reintegrationTypeModal}
        setOpenModal={setReintegrationTypeModal}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{ maxWidth: "20rem" }}
        >
          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32" />
          </div>

          <p className="mt-2 text-lg text-center text-gray-700">
            ¿Como quiere procesar el reintegro?
          </p>
          <div className="mt-4 flex flex-row justify-center gap-12">
            <PrimaryButton
              className="px-4"
              type="button"
              onClick={() => {
                setChangeModalTab(2);
                setReintegrationTypeModal(false);
                setChangeModal(true);
              }}
            >
              Reintegro vía Zelle
            </PrimaryButton>

            <PrimaryButton
              className="px-4"
              type="button"
              onClick={() => {
                if (!refundMethod) {
                  alertService.error(
                    "Método de reintegro en bolívares no habilitado"
                  );
                  return;
                }
                formik.setFieldValue("paymentMethod", refundMethod);
                formik.setFieldValue("currency", "BS");
                setReintegrationTypeModal(false);
                setRefundConfirmationModal(true);
              }}
            >
              Reintegro en Bolívares
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <ChangeModal
        formik={formik}
        owner={owner}
        remaining={remaining}
        b2pBank={b2pBank}
        bankList={bankList}
        openModal={changeModal}
        setOpenModal={setChangeModal}
        setOpenRefundModal={setRefundConfirmationModal}
        payments={payments}
        availableChange={availableChange}
        isNatural={isNatural}
        isRefund={isRefund}
        hasRetention={hasRetention}
      />
    </>
  );
};

export default PaymentChange;
